<template>
<div class="main-container">
    <div class="inner-container">
        <signed-in-user-info :user="user" :userData="userData" :loadingUser="loadingUser"/>

        <!-- Create Vendor -->
        <div class="track-shipment update-user">
            <div class="user-info">
                <div class="inner-overflow-container">
                    <div class="confirmContainerInner">
                        <div class="confirm-container-address">
                            <div class="confirmContainerInner1">
                                <div class="confirm-svg">
                                    <h2>Create Vendor</h2>
                                </div>

                                <div class="confirmLabelContainer">
                                    <label for="createVendor">Vendor Code</label>
                                    <input id="createVendor" type="text" v-model="vendorInput.vendor" @input="sanitizeUpper('vendor')" placeholder="Vendor Code">
                                </div>

                                <div class="confirmLabelContainer">
                                    <label for="createVendorName">Vendor Name</label>
                                    <input id="createVendorName" type="text" v-model="vendorInput.vendorName" placeholder="Vendor Name">
                                </div>

                                <!-- Status is not operator-selectable at creation: a newly
                                     created vendor is always "active". Status is changed later
                                     via the Vendor Details edit form. -->

                                <div class="confirmLabelContainer">
                                    <label for="createPhone">Phone Number</label>
                                    <input id="createPhone" type="text" v-model="vendorInput.phoneNumber" placeholder="Phone Number">
                                </div>

                                <div class="confirmLabelContainer">
                                    <label for="createEmail">Email</label>
                                    <input id="createEmail" type="text" v-model="vendorInput.email" placeholder="Email">
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <button class="update-user-button" @click="createVendor()">Create Vendor</button>
            </div>
        </div>

        <!-- Vendor list / search / select -->
        <div class="track-shipment update-user">
            <vendor-management-table :key="refreshKey" @selectVendor="selectVendor($event)"/>
        </div>
    </div>
</div>

<!-- Edit selected vendor -->
<vendor-info v-if="selectedVendor" :key="selectedVendor" :vendorID="selectedVendor" :jwtToken="jwtToken" @closeVendorInfo="closeVendorInfo($event)"/>
<LoadingData v-if="creatingVendor" :headerMessage="headerMessage"/>
<AlertUser v-if="toggleAlertBox" @closeAlertBox="closeAlertBox($event)" :message="alertMessage"/>
</template>

<script>
import { Auth, API } from 'aws-amplify';
import SignedInUserInfo from '../UserDashboard/SignedInUserInfo.vue';
import AlertUser from '../../../components/Popups/AlertUser.vue';
import LoadingData from '../../Popups/LoadingData.vue';
import VendorManagementTable from './VendorManagementTable.vue';
import VendorInfo from './VendorInfo.vue';

export default {
    components: {
        SignedInUserInfo,
        AlertUser,
        LoadingData,
        VendorManagementTable,
        VendorInfo,
    },
    data() {
        return {
            user: {},
            userData: {},
            loadingUser: false,
            jwtToken: null,
            AdminUser: false,
            InternalAdmin: false,
            BillingAdmin: false,
            selectedVendor: null,
            refreshKey: 0,
            creatingVendor: false,
            headerMessage: "",
            toggleAlertBox: false,
            alertMessage: null,
            vendorInput: {
                vendor: "",
                vendorName: "",
                status: "active",
                phoneNumber: "",
                email: "",
            },
        };
    },
    methods: {
        sanitizeUpper(field) {
            const value = this.vendorInput[field] || "";
            this.vendorInput[field] = value.replace(/\s/g, "").toUpperCase();
        },
        selectVendor(vendor) {
            this.selectedVendor = vendor;
        },
        closeVendorInfo() {
            this.selectedVendor = null;
        },
        closeAlertBox(toggleAlertBox) {
            this.toggleAlertBox = toggleAlertBox;
        },
        // Inline mutation that only selects the PK back to avoid AWSDateTime null issues.
        async createVendor() {
            if (!this.vendorInput.vendor) {
                this.toggleAlertBox = true;
                this.alertMessage = "Vendor code is required";
                return;
            }
            this.creatingVendor = true;
            this.headerMessage = "Creating Vendor";
            try {
                await API.graphql({
                    query: `mutation MyMutation($input: CreateVendorInput!) {
                        createVendor(input: $input) {
                            vendor
                        }
                    }`,
                    variables: {
                        input: {
                            vendor: this.vendorInput.vendor,
                            vendorName: this.vendorInput.vendorName,
                            // New vendors are always created active. Status is managed
                            // afterward via the Vendor Details edit form, not at creation.
                            status: "active",
                            phoneNumber: this.vendorInput.phoneNumber,
                            email: this.vendorInput.email,
                        },
                    },
                    authMode: "AMAZON_COGNITO_USER_POOLS",
                });
                this.toggleAlertBox = true;
                this.alertMessage = "Vendor created";
                this.vendorInput = { vendor: "", vendorName: "", status: "active", phoneNumber: "", email: "" };
                // Force the management table to reload.
                this.refreshKey++;
            } catch (e) {
                console.log(e);
                if (e && e.errors) {
                    this.toggleAlertBox = true;
                    this.alertMessage = e.errors[0].message;
                } else {
                    this.toggleAlertBox = true;
                    this.alertMessage = "Error creating vendor";
                }
            } finally {
                this.creatingVendor = false;
            }
        },
        async GetUserGraphQL() {
            this.loadingUser = true;
            try {
                let userReturnData = await API.graphql({
                    query: `query GetUser($userName: String!){
                        getUser(userName: $userName){
                            userName
                            accountNo
                            fullname
                            userEmail
                        }
                    }`,
                    variables: {
                        userName: this.user.username,
                    },
                    authMode: "AMAZON_COGNITO_USER_POOLS",
                });
                this.userData = userReturnData.data.getUser;
            } catch (error) {
                this.toggleAlertBox = true;
                this.alertMessage = "Unable to get user information";
            } finally {
                this.loadingUser = false;
            }
        },
    },
    mounted() {
        Auth.currentAuthenticatedUser().then(user => {
            this.user = user;
            this.jwtToken = user.signInUserSession.accessToken.jwtToken;
            this.GetUserGraphQL();

            const groups = this.user.signInUserSession.idToken.payload['cognito:groups'];
            if (groups) {
                groups.forEach((group) => {
                    if (group == 'Admin') this.AdminUser = true;
                    if (group == 'InternalAdmin') this.InternalAdmin = true;
                    if (group == 'BillingAdmin') this.BillingAdmin = true;
                });
            }

            // Restrict to the BillingAdmin group (Admin retains access).
            if (!this.BillingAdmin && !this.AdminUser) {
                alert("Access denied to vendor dashboard");
                this.$router.push('/ShipmentDashboard');
            }
        }).catch(error => {
            if (error) {
                this.$router.push('Login');
                Auth.signOut({ global: false });
            }
        });
    },
};
</script>

<style scoped>
html {
    background-color: #f3f3f3;
}

.main-container {
    margin-top: 20px;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
}

.inner-container {
    width: 70%;
    background-color: #fff;
    box-shadow: rgba(0, 0, 0, 0.35) 0px 5px 15px;
    border-radius: 25px;
}

.track-shipment {
    background-color: #fff;
    padding: 15px;
    border-radius: 25px;
    margin: 10px;
    box-shadow: rgba(0, 0, 0, 0.35) 0px 5px 15px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
}

.user-info {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    text-align: left;
}

.confirmLabelContainer {
    display: flex;
    flex-direction: column;
    text-align: left;
    margin-top: 10px;
}

.confirmLabelContainer label {
    font-weight: bold;
}

input, select {
    padding: 10px;
    font-size: 15px;
    border: 1px solid rgba(0, 0, 0, .336);
    border-radius: 15px;
    outline: none;
    margin-top: 5px;
}

.update-user-button {
    cursor: pointer;
    border: none;
    background-color: #32ccfe;
    color: #fff;
    padding: 10px 20px;
    border-radius: 15px;
    margin-top: 15px;
    transition: background-color 0.3s;
}

.update-user-button:hover {
    background-color: #2baeda;
}

@media only screen and (max-width: 1300px) {
    .inner-container {
        width: 85%;
    }
}

@media only screen and (max-width: 600px) {
    .inner-container {
        width: 95%;
    }
}
</style>
